<template>
    <div class="event-screen interlude w-100 h-100" v-if="interlude">
        <EventBrand class="event-interlude-brand" />
        <FullScreen btn_class="btn btn-sm btn-outline-light event-icon-btn position-absolute top-0 start-0 m-3 interlude-logout" />
        <button type="button" class="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-3 interlude-logout" title="Cerrar sesión" @click="logout"><i class="bi bi-box-arrow-left"></i></button>

        <!-- Keyed by started_at: a new animation (intro -> later a
             checkpoint) remounts and replays from scratch. -->
        <div :key="interlude.started_at" class="interlude-stage" >
            <template v-if="is_intro">
                <div class="interlude-kicker" style="animation-delay: 200ms">{{ theme.texts.intro_kicker }}</div>
                <h1 class="interlude-title" style="animation-delay: 800ms">{{ theme.texts.intro_title }}</h1>
                <div class="interlude-name" style="animation-delay: 1300ms">{{ route.stage_name }}</div>
                <div class="interlude-route">
                    <StageRoute :route="route" :theme="theme" :color="color" mode="draw"
                        :draw_delay_ms="800" :draw_ms="3000" :rider_delay_ms="200" />
                </div>
            </template>
            <template v-else>
                <div class="interlude-kicker" style="animation-delay: 100ms">
                    <i :class="'bi ' + theme.icons.stop + ' me-2'"></i>{{ current_stop ? current_stop.name : theme.texts.stop_reached }}
                </div>
                <h1 class="interlude-title" style="animation-delay: 500ms">{{ theme.texts.checkpoint_title }}</h1>
                <div class="interlude-name" style="animation-delay: 900ms">{{ route.stage_name }} &middot; {{ route.done }}/{{ route.total }}</div>
                <div class="interlude-route interlude-route-static">
                    <StageRoute :route="route" :theme="theme" :color="color" mode="ride"
                        :ride_from="ride_from" :ride_to="ride_to" :ride_delay_ms="1300" :ride_ms="2400" :rider_delay_ms="300" />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import FullScreen from '@/components/FullScreen.vue'
import EventBrand from '@/components/EventBrand.vue'
import utils from '@/../../shared/utils'
import StageRoute from '@/components/StageRoute.vue'
import { get_active_theme } from '@/themes'

const FADE_OUT_MS = 800;

// GAME_STATES.INTERLUDE's screen: the short stage animations the server
// shows between steps of a stage playback (see server.js's _show_interlude):
//  - intro: "Vamos a empezar... La etapa de hoy", the route drawing itself
//    from start to finish with every stop, the rider waiting at the start.
//  - checkpoint (reaching a stop): "Veamos cómo ha ido", the route with the
//    stretch ridden so far and the rider riding up to the stop.
// The server decides how long it lasts (interlude.duration) and moves on by
// itself; this only plays it and fades out just before.
export default {
    name: 'Interlude',
    components: { FullScreen, StageRoute, EventBrand },
    data(){
        return { FADE_OUT_MS };
    },
    computed: {
        interlude(){
            return this.$store.state.game.interlude;
        },
        route(){
            return this.interlude.route;
        },
        is_intro(){
            return this.interlude.kind == 'intro';
        },
        duration_ms(){
            return this.interlude.duration || utils.STAGE_PLAYBACK.CHECKPOINT_DURATION * 1000;
        },
        theme(){
            return get_active_theme();
        },
        // Each player sees their own team colour on the rider; the big
        // screen (manager) falls back to the theme default.
        color(){
            const user = this.$store.state.game.user;
            return (user && user.avatar_style && user.avatar_style.color) || 'var(--vc-accent-500)';
        },
        current_stop(){
            return (this.route.stops || []).find((s) => s.position == this.route.done) || null;
        },
        // Ride from the previous stop (or the start) up to this one.
        ride_from(){
            const before = (this.route.stops || []).filter((s) => s.position < this.route.done).map((s) => s.position);
            const from = before.length ? Math.max(...before) : 0;
            return from / Math.max(1, this.route.total);
        },
        ride_to(){
            return this.route.done / Math.max(1, this.route.total);
        }
    },
    mounted(){
        if(window.store.state.network == "connected")
            window.network.wso.send(JSON.stringify({action: window.RPC.UPDATE_VIEW, params: {view: window.router.currentRoute._value.name, activity: null}}));
        $('.modal-backdrop').remove();
    },
    methods: {
        logout(){
            utils.reset_client();
        }
    }
}
</script>

<style scoped>
    .interlude{
        position: relative;
        user-select: none;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: radial-gradient(circle at 50% 30%, var(--vc-ink-700), var(--vc-ink-900) 70%);
        color: var(--vc-paper-50);
    }
    .interlude-logout{
        z-index: 5;
    }
    /* Top-left corner belongs to the fullscreen button. */
    .interlude .event-interlude-brand{
        left: 68px;
    }

    .interlude-stage{
        width: min(1100px, 94%);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        animation: interlude-in 0.5s ease-out both;
        animation-play-state: running;
    }
    @keyframes interlude-out{
        from{ opacity: 1; transform: none; }
        to{ opacity: 0; transform: scale(0.96); }
    }

    .interlude-kicker,
    .interlude-title,
    .interlude-name{
        opacity: 0;
        animation: interlude-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }
    @keyframes interlude-in{
        from{ opacity: 0; transform: translateY(18px); }
        to{ opacity: 1; transform: none; }
    }

    .interlude-kicker{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: clamp(0.9rem, 2.2vw, 1.3rem);
        text-transform: uppercase;
        letter-spacing: 0.14em;
        color: var(--vc-accent-500);
    }
    .interlude-title{
        font-family: "Archivo", sans-serif;
        font-weight: 900;
        font-size: clamp(2rem, 7vw, 4.6rem);
        line-height: 1.05;
        text-transform: uppercase;
        color: #fff;
        margin: 10px 0 6px;
    }
    .interlude-name{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: clamp(1rem, 2.8vw, 1.7rem);
        color: rgba(255, 255, 255, 0.75);
    }

    .interlude-route{
        width: 100%;
        margin-top: clamp(28px, 6vh, 64px);
        /* room for the labels hanging above/below the markers (StageRoute
           may push them out a lane or two to keep them from overlapping) */
        padding: 56px 0 60px;
    }

    @media (prefers-reduced-motion: reduce){
        .interlude-kicker,
        .interlude-title,
        .interlude-name{ animation: none; opacity: 1; }
    }
</style>
